/* JsonDiffNavigationRenderer.js */

"use strict";
const FOLD_START_DISTANCE_FROM_MARKER = 3;
const _ = require("lodash");
class JsonDiffNavigationRenderer {
    constructor(model) {
        this._model = model;
        this._currentHighlightedDifferenceIndex = -1;

        this._rootElement = document.createElement("span");
        this._rootElement.classList.add("diff-navigation");
        this._rootElement.classList.add(this._model._theme || "dark");

        this._buildNavigationControls();
        this._defineDebounceAndBoundFunctions();
        this._registerListeners();
        this.render();
    }

    _buildNavigationControls() {
        this._synchronizedScrollButton = document.createElement("toggle-icon-gl");
        this._synchronizedScrollButton.classList.add("synchronized-scroll");
        this._synchronizedScrollButton.dataset.icon = "toggle.lock";
        this._synchronizedScrollButton.setAttribute("title", "Synchronized Scroll On");

        this._compactViewButton = document.createElement("actionable-icon-gl");
        this._compactViewButton.classList.add("compact-view");
        this._compactViewButton.dataset.icon = "configdrift.compare_compact";
        this._compactViewButton.setAttribute("title", "Compact View");

        this._expandedViewButton = document.createElement("actionable-icon-gl");
        this._expandedViewButton.classList.add("expanded-view");
        this._expandedViewButton.dataset.icon = "configdrift.compare_expanded";
        this._expandedViewButton.setAttribute("title", "Expanded View");

        this._findPreviousButton = document.createElement("actionable-icon-gl");
        this._findPreviousButton.classList.add("find-previous");
        this._findPreviousButton.dataset.icon = "common.chevron";
        this._findPreviousButton.setAttribute("title", "Find Previous");
        this._currentDifferenceNumberSpan = document.createElement("span");
        this._currentDifferenceNumberSpan.classList.add("current-difference-number");
        this._findNextButton = document.createElement("actionable-icon-gl");
        this._findNextButton.classList.add("find-next");
        this._findNextButton.dataset.icon = "common.chevron";
        this._findNextButton.setAttribute("title", "Find Next");
    }
    /*******************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /*******************************************/
    _defineDebounceAndBoundFunctions() {
        this._onLeftEditorScrollTopDebounce = _.debounce(
            this._onLeftEditorScrollTop.bind(this),
            10,
            {trailing: true, maxWaits: 50}
        );
        this._onRightEditorScrollTopDebounce = _.debounce(
            this._onRightEditorScrollTop.bind(this),
            10,
            {trailing: true, maxWaits: 50}
        );

        this._onLeftEditorScrollLeftDebounce = _.debounce(
            this._onLeftEditorScrollLeft.bind(this),
            50,
            {trailing: true, maxWaits: 50}
        );
        this._onRightEditorScrollLeftDebounce = _.debounce(
            this._onRightEditorScrollLeft.bind(this),
            50,
            {trailing: true, maxWaits: 50}
        );

        this._onLeftEditorMouseMoveDebounce = _.debounce(
            this._onLeftEditorMouseMove.bind(this),
            500
        );
        this._onRightEditorMouseMoveDebounce = _.debounce(
            this._onRightEditorMouseMove.bind(this),
            500
        );

        this._onChangeFoldLeftEditor = this._onChangeFoldLeftEditor.bind(this);
        this._onChangeFoldRightEditor = this._onChangeFoldRightEditor.bind(this);

        this._leftEditorOnMouseEnter = this._leftEditorOnMouseEnter.bind(this);
        this._rightEditorOnMouseEnter = this._rightEditorOnMouseEnter.bind(this);
    }

    _registerListeners() {
        this._model.on("markers", (this._onMarkers = this._onMarkers.bind(this)));
        this._model.on("leftEditor", (this._onLeftEditor = this._onLeftEditor.bind(this)));
        this._model.on("rightEditor", (this._onRightEditor = this._onRightEditor.bind(this)));
        this._model.on(
            "scrollSynchronized",
            (this._setSynchronizedScroll = this._setSynchronizedScroll.bind(this))
        );
        this._model.on("compactView", (this._setCompactView = this._setCompactView.bind(this)));
        this._model.on(
            "sideBySideView",
            (this._setSideBySideView = this._setSideBySideView.bind(this))
        );

        document.addEventListener(
            "scroll",
            (this._onScrollOutsideEditor = this._onScrollOutsideEditor.bind(this))
        );
        this._findNextButton.addEventListener(
            "click",
            (this._onFindNextButtonClick = this._onFindNextButtonClick.bind(this))
        );
        this._findPreviousButton.addEventListener(
            "click",
            (this._onFindPreviousButtonClick = this._onFindPreviousButtonClick.bind(this))
        );
        this._synchronizedScrollButton.addEventListener(
            "click",
            (this._onSynchronizedScrollButtonClick =
                this._onSynchronizedScrollButtonClick.bind(this))
        );
        this._compactViewButton.addEventListener(
            "click",
            (this._onCompactViewButtonClick = this._onCompactViewButtonClick.bind(this))
        );
        this._expandedViewButton.addEventListener(
            "click",
            (this._onExpandedViewButtonClick = this._onExpandedViewButtonClick.bind(this))
        );

        this._registerMouseEventListeners();
        this._registerChangeFoldListeners();

        this._model.leftEditor.on("mousemove", this._onLeftEditorMouseMoveDebounce);
        this._model.rightEditor.on("mousemove", this._onRightEditorMouseMoveDebounce);
    }

    _unregisterListeners() {
        this._model.removeListener("markers", (this._onMarkers = this._onMarkers.bind(this)));
        this._model.removeListener(
            "leftEditor",
            (this._onLeftEditor = this._onLeftEditor.bind(this))
        );
        this._model.removeListener(
            "rightEditor",
            (this._onRightEditor = this._onRightEditor.bind(this))
        );
        this._model.removeListener("scrollSynchronized", this._setSynchronizedScroll);
        this._model.removeListener("compactView", this._setCompactView);
        this._model.on("sideBySideView", this._setSideBySideView);

        document.removeEventListener("scroll", this._onScrollOutsideEditor);
        this._findNextButton.removeEventListener("click", this._onFindNextButtonClick);
        this._findPreviousButton.removeEventListener("click", this._onFindPreviousButtonClick);
        this._synchronizedScrollButton.removeEventListener(
            "click",
            this._onSynchronizedScrollButtonClick
        );
        this._compactViewButton.removeEventListener("click", this._onCompactViewButtonClick);
        this._expandedViewButton.removeEventListener("click", this._onExpandedViewButtonClick);

        this._unregisterMouseEventListeners();
        this._unregisterScrollTopListeners();
        this._unregisterChangeFoldListeners();

        this._model.leftEditor.removeEventListener(
            "mousemove",
            this._onLeftEditorMouseMoveDebounce
        );
        this._model.rightEditor.removeEventListener(
            "mousemove",
            this._onRightEditorMouseMoveDebounce
        );
    }

    _registerLeftEditorListeners() {
        this._model.leftEditor.container.addEventListener(
            "mouseenter",
            this._leftEditorOnMouseEnter
        );
    }

    _registerRightEditorListeners() {
        this._model.rightEditor.container.addEventListener(
            "mouseenter",
            this._rightEditorOnMouseEnter
        );
    }

    _registerMouseEventListeners() {
        this._registerLeftEditorListeners();
        this._registerRightEditorListeners();
    }

    _registerScrollTopListeners(listenLeftScrollTopListener) {
        this._unregisterScrollTopListeners();

        if (listenLeftScrollTopListener) {
            // console.log("registering left scroll top listeners");
            this._model.leftEditor.session.on(
                "changeScrollTop",
                this._onLeftEditorScrollTopDebounce
            );
            this._model.leftEditor.session.on(
                "changeScrollLeft",
                this._onLeftEditorScrollLeftDebounce
            );
        } else {
            // console.log("registering right scroll top listeners");
            this._model.rightEditor.session.on(
                "changeScrollTop",
                this._onRightEditorScrollTopDebounce
            );
            this._model.rightEditor.session.on(
                "changeScrollLeft",
                this._onRightEditorScrollLeftDebounce
            );
        }
    }

    _registerChangeFoldListeners() {
        this._model.leftEditor.session.addEventListener("changeFold", this._onChangeFoldLeftEditor);
        this._model.rightEditor.session.addEventListener(
            "changeFold",
            this._onChangeFoldRightEditor
        );
    }

    _registerRightEditorScrollTopAndFoldListeners() {
        this._model.rightEditor.session.addEventListener(
            "changeScrollTop",
            this._onRightEditorScrollTopDebounce
        );
        this._model.rightEditor.session.addEventListener(
            "changeFold",
            this._onChangeFoldRightEditor
        );
    }

    _unregisterMouseEventListeners() {
        this._model.leftEditor.container.removeEventListener(
            "mouseenter",
            this._leftEditorOnMouseEnter
        );
        this._model.rightEditor.container.removeEventListener(
            "mouseenter",
            this._rightEditorOnMouseEnter
        );
    }

    _unregisterScrollTopListeners() {
        // console.log("unregistering scroll top listeners");
        this._model.leftEditor.session.removeListener(
            "changeScrollTop",
            this._onLeftEditorScrollTopDebounce
        );
        this._model.rightEditor.session.removeListener(
            "changeScrollTop",
            this._onRightEditorScrollTopDebounce
        );
        this._model.leftEditor.session.on("changeScrollLeft", this._onLeftEditorScrollLeftDebounce);
        this._model.rightEditor.session.on(
            "changeScrollLeft",
            this._onRightEditorScrollLeftDebounce
        );
    }

    _unregisterChangeFoldListeners() {
        this._model.leftEditor.session.removeListener("changeFold", this._onChangeFoldLeftEditor);
        this._model.rightEditor.session.removeListener("changeFold", this._onChangeFoldRightEditor);
    }

    _unregisterRightEditorScrollTopAndFoldListeners() {
        this._model.rightEditor.session.removeListener(
            "changeScrollTop",
            this._onRightEditorScrollTopDebounce
        );
        this._model.rightEditor.session.removeListener("changeFold", this._onChangeFoldRightEditor);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();

        this._currentHighlightedDifferenceIndex = undefined;
        this._editorTop = undefined;
        this._leftEditorLastMousePositionFromScrollTop = undefined;
        this._rightEditorLastMousePositionFromScrollTop = undefined;
        this._leftEditorLastMouseY = undefined;
        this._rightEditorLastMouseY = undefined;
        this._findNextButton = undefined;
        this._findPreviousButton = undefined;
        this._synchronizedScrollButton = undefined;
        this._compactViewButton = undefined;
        this._expandedViewButton = undefined;
        this._currentDifferenceNumberSpan = undefined;

        this._onLeftEditorScrollTopDebounce = undefined;
        this._onRightEditorScrollTopDebounce = undefined;
        this._onLeftEditorMouseMoveDebounce = undefined;
        this._onRightEditorMouseMoveDebounce = undefined;
        this._onLeftEditorScrollLeftDebounce = undefined;
        this._onRighEditorScrollLeftDebounce = undefined;

        this._leftEditorLastMouseY = undefined;
        this._rightEditorLastMouseY = undefined;
        this._onChangeFoldLeftEditor = undefined;
        this._onChangeFoldRightEditor = undefined;

        this._rootElement.remove();
        this._rootElement = undefined;

        return Promise.resolve();
    }

    render() {
        this._rootElement.appendChild(this._synchronizedScrollButton);
        this._rootElement.appendChild(this._compactViewButton);
        this._rootElement.appendChild(this._expandedViewButton);
        this._rootElement.appendChild(this._findPreviousButton);
        this._rootElement.appendChild(this._currentDifferenceNumberSpan);
        this._rootElement.appendChild(this._findNextButton);

        this._enableOrDisableNavigationButtons();
        this._updateCurrentDifferenceCount(undefined, this._model.markers.length);
        this._setSynchronizedScroll();
        this._setSideBySideView();
        this._setCompactView();
        setTimeout(() => {
            this._editorTop = this._model.leftEditor.container.getBoundingClientRect().top;
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElement;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* START PUBLIC API METHODS */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    _onMarkers() {
        //redo synchronizations or reset any variables
        this._currentHighlightedDifferenceIndex = -1;
        this._enableOrDisableNavigationButtons();
        this._updateCurrentDifferenceCount(undefined, this._model.markers.length);
        if (this._model.compactView) {
            this._foldUnchangedLines();
        }
    }

    /**
     * On left editor change in model, register listeners on the new editor
     * @private
     */
    _onLeftEditor() {
        this._registerLeftEditorListeners();
    }

    /**
     * On right editor change in model, register listeners on the new editor
     * @private
     */
    _onRightEditor() {
        this._registerRightEditorListeners();
    }

    _setSynchronizedScroll() {
        this._toggleSynchronizedScroll();
    }

    _setCompactView() {
        this._toggleCompactView();
    }

    _setSideBySideView() {
        this._toggleSideBySideView();
    }

    _onScrollOutsideEditor(/*event*/) {
        this._editorTop = this._model.leftEditor.container.getBoundingClientRect().top;
    }

    _leftEditorOnMouseEnter(mouseEvent) {
        this._leftEditorLastMouseY = mouseEvent.clientY;
        this._registerScrollTopListeners(true);
    }

    _rightEditorOnMouseEnter(mouseEvent) {
        this._rightEditorLastMouseY = mouseEvent.clientY;
        this._registerScrollTopListeners(false);
    }

    _onLeftEditorMouseMove(event) {
        let leftEditorLastMousePosition = event.getDocumentPosition();
        this._leftEditorLastMouseY = event.clientY;
        // console.log("mouse position row based on clientY " + (event.clientY/this._model.leftEditor.renderer.lineHeight));
        // console.log("%cnew mouse position after mouse move on left " + JSON.stringify(leftEditorLastMousePosition) , 'background-color:orange;');
        if (this._model.sideBySideView) {
            this._highlightMarkersAtMousePositionInSideBySideView(
                leftEditorLastMousePosition,
                true
            );
        } else {
            this._highlightMarkersAtMousePositionInSinglePaneView(leftEditorLastMousePosition);
        }
    }

    _onRightEditorMouseMove(event) {
        let rightEditorLastMousePosition = event.getDocumentPosition();
        this._rightEditorLastMouseY = event.clientY;
        // console.log("%cnew mouse position after mouse move on right " + JSON.stringify(rightEditorLastMousePosition) , 'background-color:orange;');
        this._highlightMarkersAtMousePositionInSideBySideView(rightEditorLastMousePosition, false);
    }

    _onLeftEditorScrollTop(scrollTop) {
        this._leftEditorLastMousePositionFromScrollTop =
            this._leftEditorLastMouseY - this._editorTop;
        // console.log("_leftEditorLastMousePositionFromScrollTop: " + this._leftEditorLastMousePositionFromScrollTop);
        let leftEditorLastMousePositionRow = this._getMousePositionFromScrollTop(scrollTop, true);
        // console.log("new mouse position on left " + leftEditorLastMousePositionRow);
        // console.log("new scroll top after scroll on left " + scrollTop);
        if (this._model.sideBySideView) {
            if (this._model.scrollSynchronized) {
                this._syncScrollRightEditor(scrollTop);
            }
            this._highlightMarkersAtMousePositionInSideBySideView(
                {row: leftEditorLastMousePositionRow, column: 0},
                true
            );
        } else {
            this._highlightMarkersAtMousePositionInSinglePaneView({
                row: leftEditorLastMousePositionRow,
                column: 0,
            });
        }
        // console.log("new mouse position on left " + JSON.stringify(leftEditorLastMousePositionRow));
        // console.log("new scroll top after scroll on left " + scrollTop);
    }

    _onLeftEditorScrollLeft(scrollLeft) {
        let maxScrollLeft =
            this._model.rightEditor.renderer.layerConfig.width -
            this._model.rightEditor.renderer.$size.scrollerWidth +
            this._model.rightEditor.renderer.scrollMargin.right;
        let newScrollLeft = Math.min(scrollLeft, maxScrollLeft);
        let currentScrollLeft = this._model.rightEditor.session.getScrollLeft();
        if (newScrollLeft !== currentScrollLeft) {
            this._model.rightEditor.renderer.scrollToX(newScrollLeft);
        }
    }

    _onRightEditorScrollLeft(scrollLeft) {
        let maxScrollLeft =
            this._model.leftEditor.renderer.layerConfig.width -
            this._model.leftEditor.renderer.$size.scrollerWidth +
            this._model.leftEditor.renderer.scrollMargin.right;
        let newScrollLeft = Math.min(scrollLeft, maxScrollLeft);
        let currentScrollLeft = this._model.leftEditor.session.getScrollLeft();
        if (newScrollLeft !== currentScrollLeft) {
            this._model.leftEditor.renderer.scrollToX(newScrollLeft);
        }
    }

    _getMousePositionFromScrollTop(scrollTop, isLeftEditor = true) {
        let lineHeight = this._model.leftEditor.renderer.lineHeight;
        let lastMousePositionFromScrollTop = isLeftEditor
            ? this._leftEditorLastMousePositionFromScrollTop
            : this._rightEditorLastMousePositionFromScrollTop;
        let editorLastMousePositionRow = (scrollTop + lastMousePositionFromScrollTop) / lineHeight;
        editorLastMousePositionRow =
            Math.floor(editorLastMousePositionRow) + 0.5 <= editorLastMousePositionRow
                ? Math.floor(editorLastMousePositionRow)
                : Math.round(editorLastMousePositionRow);
        return editorLastMousePositionRow;
    }

    _onRightEditorScrollTop(scrollTop) {
        if (this._model.scrollSynchronized) {
            this._syncScrollLeftEditor(scrollTop);
        }
        this._rightEditorLastMousePositionFromScrollTop =
            this._rightEditorLastMouseY - this._editorTop;
        let rightEditorLastMousePositionRow = this._getMousePositionFromScrollTop(scrollTop, false);
        // console.log("new mouse position on right " + rightEditorLastMousePositionRow);
        // console.log("new scroll top after scroll on right " + scrollTop);
        this._highlightMarkersAtMousePositionInSideBySideView(
            {row: rightEditorLastMousePositionRow, column: 0},
            false
        );
    }

    _onChangeFoldLeftEditor(foldEvent) {
        if (foldEvent.action === "add") {
            this._foldRightEditorLines(foldEvent.data);
        } else {
            this._unfoldRightEditorLines(foldEvent.data);
        }
    }

    _onChangeFoldRightEditor(foldEvent) {
        if (foldEvent.action === "add") {
            this._foldLeftEditorLines(foldEvent.data);
        } else {
            this._unfoldLeftEditorLines(foldEvent.data);
        }
    }

    /**
     * Fired when find next button is clicked
     * @private
     */
    _onFindNextButtonClick() {
        this._highlightNextDifference();
    }

    /**
     * Fired when find previous button is clicked
     * @private
     */
    _onFindPreviousButtonClick() {
        this._highlightPreviousDifference();
    }

    _onSynchronizedScrollButtonClick() {
        this._model.setScrollSynchronized(!this._model.scrollSynchronized);
        this._model._triggerScrollSynchronizedValueChanged(this._model.scrollSynchronized);
    }

    _onCompactViewButtonClick() {
        this._expandedViewButton.setActive(false);
        this._compactViewButton.setActive(true);
        this._model._compactView = true;
        //change folding rules to fold unchanged text & collapse new folds
        this._foldUnchangedLines();
    }

    _onExpandedViewButtonClick() {
        this._compactViewButton.setActive(false);
        this._expandedViewButton.setActive(true);
        this._model._compactView = false;
        //change folding rules to json folding rules & expand all folds
        this._unfoldUnchangedLines();
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _toggleSynchronizedScroll() {
        if (this._model.isScrollSynchronized()) {
            this._enableSynchronizedScroll();
        } else {
            this._disableSynchronizedScroll();
        }
    }

    _toggleCompactView() {
        if (this._model.compactView) {
            this._foldUnchangedLines();
        } else {
            this._unfoldUnchangedLines();
        }
    }

    _toggleSideBySideView() {
        if (this._model.sideBySideView) {
            this._registerChangeFoldListeners();
            this._synchronizedScrollButton.disabled = false;
        } else {
            this._unregisterChangeFoldListeners();
            this._disableSynchronizedScroll();
            this._synchronizedScrollButton.disabled = true;
        }
        this._removeActiveDifferenceMarkers();
    }

    _enableSynchronizedScroll() {
        this._synchronizedScrollButton.setToggled(false);
        this._synchronizedScrollButton.title = "Synchronized Scroll Off";
    }

    _disableSynchronizedScroll() {
        this._synchronizedScrollButton.setToggled(true);
        this._synchronizedScrollButton.title = "Synchronized Scroll On";
    }

    _foldUnchangedLines() {
        this._unregisterChangeFoldListeners();
        this._model.leftEditor.session.foldAll();
        if (this._model.sideBySideView || this._model.rightEditor) {
            this._model.rightEditor.session.foldAll();
            this._registerChangeFoldListeners();
        }
    }

    _unfoldUnchangedLines() {
        this._unregisterChangeFoldListeners();
        this._model.leftEditor.session.unfold();
        if (this._model.sideBySideView || this._model.rightEditor) {
            this._model.rightEditor.session.unfold();
            this._registerChangeFoldListeners();
        }
    }

    _syncScrollLeftEditor(rightScrollTop) {
        let rightFirstVisibleRow = this._model.rightEditor.renderer.getFirstVisibleRow();
        if (rightScrollTop === 0) {
            this._model.leftEditor.renderer.scrollBy(
                0,
                -this._model.leftEditor.session.getScrollTop()
            );
            return;
        }

        let focusRowFromVisibleTop = this._getFocusRowFromTop(this._model.rightEditor);
        let rightFocusRow = focusRowFromVisibleTop + rightFirstVisibleRow;
        let leftNewScrollTop = undefined;
        let markersAtFocusRow = this._getMarkersAtFocusRowBy(rightFocusRow, false);

        // console.log("rightFocusRow: " + rightFocusRow);
        if (!markersAtFocusRow) {
            // find path at focus row on right & scroll same path on left to focus row
            leftNewScrollTop = this._syncUnchangedLinesOnLeft(rightFocusRow);
        } else if (markersAtFocusRow.right.clazz === "removed-line") {
            //scroll entire removed block past focus row on left editor
            leftNewScrollTop =
                this._model.leftEditor.renderer.lineHeight *
                (markersAtFocusRow.left.range.end.row - focusRowFromVisibleTop + 1);
            // console.log("leftTargetFirstVisibleRow: " + (markersAtFocusRow.left.range.end.row-focusRowFromVisibleTop+1));
        } else if (markersAtFocusRow.right.clazz === "added") {
            //do not scroll added-line on left until added block is above focus row on right
            let leftTargetFirstVisibleRow =
                markersAtFocusRow.left.range.start.row - focusRowFromVisibleTop - 1;
            // console.log("leftTargetFirstVisibleRow: " + leftTargetFirstVisibleRow);
            leftNewScrollTop =
                leftTargetFirstVisibleRow * this._model.leftEditor.renderer.lineHeight;
            leftNewScrollTop += rightScrollTop % this._model.rightEditor.renderer.lineHeight;
        } else if (markersAtFocusRow.right.clazz === "updated-background") {
            leftNewScrollTop = this._syncUpdatedLinesOnLeft(
                markersAtFocusRow,
                rightFocusRow,
                focusRowFromVisibleTop
            );
        }
        this._setNewScrollTop(leftNewScrollTop, this._model.leftEditor);
        // console.log("%c******************** end of syncing left with right ***************%c", 'background: green; color: white; display: block;');
    }

    _syncUnchangedLinesOnLeft(rightFocusRow) {
        let leftNewScrollTop;
        let lastDiffBeforeFirstVisibleRowOnRight = this._getLastDiffBeforeCurrentFocusRow(
            rightFocusRow,
            false
        );
        if (!lastDiffBeforeFirstVisibleRowOnRight) {
            leftNewScrollTop = this._model.rightEditor.renderer.getScrollTop();
        } else {
            let rowDifference =
                lastDiffBeforeFirstVisibleRowOnRight.right.range.end.row -
                lastDiffBeforeFirstVisibleRowOnRight.left.range.end.row;
            rowDifference =
                lastDiffBeforeFirstVisibleRowOnRight.right.clazz === "removed-line"
                    ? rowDifference - 1
                    : rowDifference;
            rowDifference =
                lastDiffBeforeFirstVisibleRowOnRight.left.clazz === "added-line"
                    ? rowDifference + 1
                    : rowDifference;
            leftNewScrollTop =
                (this._model.rightEditor.renderer.getFirstVisibleRow() - rowDifference) *
                this._model.leftEditor.renderer.lineHeight;
            leftNewScrollTop +=
                this._model.rightEditor.renderer.getScrollTop() %
                this._model.rightEditor.renderer.lineHeight;
        }
        return leftNewScrollTop;
    }

    _syncUnchangedLinesOnRight(leftFocusRow) {
        let rightNewScrollTop;
        let lastDiffBeforeFirstVisibleRowOnRight = this._getLastDiffBeforeCurrentFocusRow(
            leftFocusRow,
            true
        );
        // console.log("last difference right range: " + lastDiffBeforeFirstVisibleRowOnRight.right.range);
        // console.log("last difference left range: " + lastDiffBeforeFirstVisibleRowOnRight.left.range);
        if (!lastDiffBeforeFirstVisibleRowOnRight) {
            rightNewScrollTop = this._model.leftEditor.renderer.getScrollTop();
        } else {
            let rowDifference =
                lastDiffBeforeFirstVisibleRowOnRight.left.range.end.row -
                lastDiffBeforeFirstVisibleRowOnRight.right.range.end.row;
            rowDifference =
                lastDiffBeforeFirstVisibleRowOnRight.right.clazz === "removed-line"
                    ? rowDifference + 1
                    : rowDifference;
            rightNewScrollTop =
                (this._model.leftEditor.renderer.getFirstVisibleRow() - rowDifference) *
                this._model.rightEditor.renderer.lineHeight;
            rightNewScrollTop +=
                this._model.leftEditor.renderer.getScrollTop() %
                this._model.leftEditor.renderer.lineHeight;
        }
        return rightNewScrollTop;
    }

    _syncUpdatedLinesOnLeft(markersAtFocusRow, rightFocusRow, focusRowFromVisibleTop) {
        let leftTargetFirstVisibleRow;
        let markerLinesOnLeft =
            markersAtFocusRow.left.range.end.row - markersAtFocusRow.left.range.start.row + 1;
        if (rightFocusRow === markersAtFocusRow.right.range.end.row) {
            leftTargetFirstVisibleRow =
                markersAtFocusRow.left.range.end.row - focusRowFromVisibleTop;
        } else {
            let diffLinesFromStartOfFocusRow =
                rightFocusRow - markersAtFocusRow.right.range.start.row;
            if (diffLinesFromStartOfFocusRow <= markerLinesOnLeft) {
                let leftFocusRow =
                    markersAtFocusRow.left.range.start.row + diffLinesFromStartOfFocusRow;
                leftTargetFirstVisibleRow = leftFocusRow - focusRowFromVisibleTop;
            } else {
                leftTargetFirstVisibleRow =
                    markersAtFocusRow.left.range.end.row - focusRowFromVisibleTop;
            }
        }
        let leftNewScrollTop =
            leftTargetFirstVisibleRow * this._model.leftEditor.renderer.lineHeight;
        leftNewScrollTop +=
            this._model.rightEditor.renderer.getScrollTop() %
            this._model.rightEditor.renderer.lineHeight;
        // console.log("leftTargetFirstVisibleRow: " + leftTargetFirstVisibleRow);
        return leftNewScrollTop;
    }

    _syncUpdatedLinesOnRight(markersAtFocusRow, leftFocusRow, focusRowFromVisibleTop) {
        let rightTargetFirstVisibleRow;
        let markerLinesOnRight =
            markersAtFocusRow.right.range.end.row - markersAtFocusRow.right.range.start.row + 1;
        if (leftFocusRow === markersAtFocusRow.left.range.end.row) {
            rightTargetFirstVisibleRow =
                markersAtFocusRow.right.range.end.row - focusRowFromVisibleTop;
        } else {
            let diffLinesFromStartOfFocusRow =
                leftFocusRow - markersAtFocusRow.left.range.start.row;
            if (diffLinesFromStartOfFocusRow <= markerLinesOnRight) {
                let rightFocusRow =
                    markersAtFocusRow.right.range.start.row + diffLinesFromStartOfFocusRow;
                rightTargetFirstVisibleRow = rightFocusRow - focusRowFromVisibleTop;
            } else {
                rightTargetFirstVisibleRow =
                    markersAtFocusRow.right.range.end.row - focusRowFromVisibleTop;
            }
        }
        let rightNewScrollTop =
            rightTargetFirstVisibleRow * this._model.rightEditor.renderer.lineHeight;
        rightNewScrollTop +=
            this._model.leftEditor.renderer.getScrollTop() %
            this._model.leftEditor.renderer.lineHeight;
        // console.log("rightTargetFirstVisibleRow: " + rightTargetFirstVisibleRow);
        return rightNewScrollTop;
    }

    _syncScrollRightEditor(leftScrollTop) {
        let leftFirstVisibleRow = this._model.leftEditor.renderer.getFirstVisibleRow();
        // console.log("leftFirstVisibleRow : " + leftFirstVisibleRow);
        if (leftScrollTop === 0) {
            this._model.rightEditor.renderer.scrollBy(
                0,
                -this._model.rightEditor.session.getScrollTop()
            );
            // console.log("right editor reached first row as scroll top - setting 0 as first row on left");
            return;
        }
        let focusRowFromVisibleTop = this._getFocusRowFromTop(this._model.leftEditor);
        let leftFocusRow = leftFirstVisibleRow + focusRowFromVisibleTop;
        let markersAtFocusRow = this._getMarkersAtFocusRowBy(leftFocusRow, true);
        let rightNewScrollTop;
        // console.log("leftFocusRow : " + leftFocusRow);
        // console.log("left marker range at focus row: " + _.get(markersAtFocusRow, 'left.range'));
        // console.log("right marker range at focus row: " + _.get(markersAtFocusRow, 'right.range'));

        if (!markersAtFocusRow) {
            rightNewScrollTop = this._syncUnchangedLinesOnRight(leftFocusRow);
        } else if (markersAtFocusRow.left.clazz === "added-line") {
            //scroll entire added block past focus row on right editor
            rightNewScrollTop =
                this._model.rightEditor.renderer.lineHeight *
                (markersAtFocusRow.right.range.end.row - focusRowFromVisibleTop + 1);
            // console.log("rightTargetFirstVisibleRow: " + (markersAtFocusRow.right.range.end.row-focusRowFromVisibleTop));
        } else if (markersAtFocusRow.left.clazz === "removed") {
            //do not scroll removed-line on right until removed block is above focus row on left
            let rightTargetFirstVisibleRow =
                markersAtFocusRow.right.range.start.row - focusRowFromVisibleTop - 1;
            // console.log("rightTargetFirstVisibleRow: " + rightTargetFirstVisibleRow);
            rightNewScrollTop =
                rightTargetFirstVisibleRow * this._model.rightEditor.renderer.lineHeight;
            rightNewScrollTop += leftScrollTop % this._model.leftEditor.renderer.lineHeight;
        } else if (markersAtFocusRow.left.clazz === "updated-background") {
            rightNewScrollTop = this._syncUpdatedLinesOnRight(
                markersAtFocusRow,
                leftFocusRow,
                focusRowFromVisibleTop
            );
        }
        this._setNewScrollTop(rightNewScrollTop, this._model.rightEditor);
        // console.log("%c******************** end of syncing right with left ***************%c", 'background: red; color: black; display: block;');
    }

    /***
     * find row at top 1/3rd of right editor - make it focus row
     * on this row differences will be aligned on left, right editors
     * @param editor
     * @returns {number}
     * @private
     */
    _getFocusRowFromTop(editor) {
        let focusRowFromVisibleTop = Math.floor(
            editor.container.offsetHeight / (editor.renderer.lineHeight * 3)
        );
        // console.log("focusRowFromVisibleTop: " + focusRowFromVisibleTop);
        return focusRowFromVisibleTop;
    }

    _getMarkersAtFocusRowBy(focusRow, isFocusOnLeftEditor) {
        let criteriaToFindMarkersBy = isFocusOnLeftEditor ? "left.range" : "right.range";
        let markersAtFocusRow = this._model.markers.find((currentMarkers) => {
            let rangeToCompare = _.get(currentMarkers, criteriaToFindMarkersBy, {});
            if (currentMarkers.right.clazz === "added") {
                return rangeToCompare.contains(focusRow, 0);
            } else {
                return rangeToCompare.contains(focusRow, 0) && !rangeToCompare.isEnd(focusRow, 0);
            }
        });

        //console.log("marker type on right : " + _.get(markersAtFocusRow, 'right.clazz', 'no marker'));
        // if(markersAtFocusRow) {
        //     console.log("right marker range at focus " + _.get(markersAtFocusRow, 'right.range', 'no marker'));
        //     console.log("left marker range at focus " + _.get(markersAtFocusRow, 'left.range', 'no marker'));
        // }
        return markersAtFocusRow;
    }

    _getLastDiffBeforeCurrentFocusRow(focusRow, isFocusOnLeftEditor) {
        let criteriaToFindMarkersBy = (isFocusOnLeftEditor ? "left" : "right") + ".range.end.row";
        let focusRowForComparison = {};
        focusRowForComparison = _.set(focusRowForComparison, criteriaToFindMarkersBy, focusRow);
        let lastDiffIndex = _.sortedLastIndexBy(
            this._model.markers,
            focusRowForComparison,
            criteriaToFindMarkersBy
        );
        let lastDiffBeforeFirstVisibleRow =
            lastDiffIndex > 0 && lastDiffIndex < this._model.markers.length
                ? this._model.markers[lastDiffIndex - 1]
                : undefined;
        // console.log("last diff on right : " + JSON.stringify(_.get(lastDiffBeforeFirstVisibleRow, 'right.range', 'no-last-right-diff')));
        // console.log("last diff on left : " + JSON.stringify(_.get(lastDiffBeforeFirstVisibleRow, 'left.range', 'no-left-last-diff')));
        return lastDiffBeforeFirstVisibleRow;
    }

    _setNewScrollTop(newScrollTop, targetEditor) {
        if (!newScrollTop) {
            return;
        }
        let currentScrollTop = targetEditor.renderer.getScrollTop();
        let maxScrollTop =
            targetEditor.renderer.layerConfig.maxHeight -
            targetEditor.renderer.$size.scrollerHeight +
            targetEditor.renderer.scrollMargin.bottom;
        let normalizedNewScrollTop = newScrollTop >= 0 ? newScrollTop : 0;
        normalizedNewScrollTop = Math.min(maxScrollTop, normalizedNewScrollTop);
        if (normalizedNewScrollTop !== currentScrollTop) {
            targetEditor.renderer.scrollToY(normalizedNewScrollTop);
        }
    }

    _highlightNextDifference() {
        if (_.isEmpty(this._model.markers)) {
            return;
        }
        let differencesCount = this._model.markers.length;
        //improved highlight difference
        if (this._currentHighlightedDifferenceIndex < differencesCount - 1) {
            this._currentHighlightedDifferenceIndex++;
            let markersToHighlight = this._model.markers[this._currentHighlightedDifferenceIndex];
            this._alignLeftAndRightEditorCurrentDifferencesIfPossible(markersToHighlight);
            this._updateCurrentDifferenceCount(
                this._currentHighlightedDifferenceIndex + 1,
                differencesCount
            );
            //update connectors or current difference highlight markers
        }
    }

    _highlightPreviousDifference() {
        if (this._currentHighlightedDifferenceIndex > 0) {
            this._currentHighlightedDifferenceIndex--;
            let markersToHighlight = this._model.markers[this._currentHighlightedDifferenceIndex];
            this._alignLeftAndRightEditorCurrentDifferencesIfPossible(markersToHighlight);
            this._updateCurrentDifferenceCount(
                this._currentHighlightedDifferenceIndex + 1,
                this._model.markers.length
            );
        }
    }

    _updateCurrentDifferenceCount(currentHighlightedDifferenceCount, differencesCount) {
        if (differencesCount === 0) {
            this._currentDifferenceNumberSpan.innerText = "No Differences";
        } else {
            let currentDifferenceNumberText = currentHighlightedDifferenceCount
                ? `${currentHighlightedDifferenceCount}/${this._model.markers.length}`
                : this._model.markers.length;
            this._currentDifferenceNumberSpan.style["min-width"] =
                ((differencesCount + "").length * 2 + 1) * 0.633 + 0.4 + "rem";
            this._currentDifferenceNumberSpan.innerText = currentDifferenceNumberText;
        }
    }

    _alignLeftAndRightEditorCurrentDifferencesIfPossible(markersToHighlight) {
        //disable change scroll top listeners
        if (this._model.sideBySideView) {
            this._scrollToMarkersInSideBySideView(markersToHighlight);
            this._addActiveDifferenceMarkersInSideBySideView(markersToHighlight);
        } else {
            this._scrollToMarkersInSinglePaneView(markersToHighlight);
        }
        //enable change scroll top listeners
        this._enableOrDisableNavigationButtons();
    }

    _scrollToMarkersInSideBySideView(markersToHighlight) {
        this._unregisterScrollTopListeners();
        // this._unregisterMouseEventListeners();
        setTimeout(() => {
            this._navigateToRow(markersToHighlight.left.range.start.row, this._model.leftEditor);
            this._navigateToRow(markersToHighlight.right.range.start.row, this._model.rightEditor);
            setTimeout(() => {
                let {leftScrollDistance, rightScrollDistance} =
                    this._calculateScrollDistances(markersToHighlight);
                this._model.leftEditor.focus();
                this._model.leftEditor.renderer.scrollBy(0, leftScrollDistance);
                this._model.rightEditor.focus();
                this._model.rightEditor.renderer.scrollBy(0, rightScrollDistance);
            }, 10);
        }, 10);
    }

    _scrollToMarkersInSinglePaneView(markersToHighlight) {
        this._unregisterScrollTopListeners();
        this._removeActiveDifferenceMarkers();
        setTimeout(() => {
            //console.log("%c adding active markers in single pane view", "background-color: blue;");
            let {leftMarkerClass, rightMarkerClass} =
                this._getActiveDifferenceMarkerClass(markersToHighlight);
            if (markersToHighlight.right !== undefined) {
                this._navigateToRow(
                    markersToHighlight.right.range.start.row,
                    this._model.leftEditor
                );
                this._activeMarkers.right = this._model.leftEditor.session.addMarker(
                    markersToHighlight.right.range,
                    rightMarkerClass,
                    "fullLine",
                    false
                );
            } else if (markersToHighlight.left !== undefined) {
                this._navigateToRow(
                    markersToHighlight.left.range.start.row,
                    this._model.leftEditor
                );
                this._activeMarkers.left = this._model.leftEditor.session.addMarker(
                    markersToHighlight.left.range,
                    leftMarkerClass,
                    "fullLine",
                    false
                );
            }
            if (markersToHighlight.left !== undefined && markersToHighlight.right !== undefined) {
                let firstVisibleRow = this._model.leftEditor.renderer.getFirstVisibleRow();
                let lastVisibleRow = this._model.leftEditor.renderer.getLastVisibleRow();
                //if editor can show both left & right diffs markers start rows, scroll right editor marker into view
                if (
                    markersToHighlight.right.range.start.row -
                        markersToHighlight.left.range.start.row <=
                    lastVisibleRow - firstVisibleRow
                ) {
                    this._navigateToRow(
                        markersToHighlight.left.range.start.row,
                        this._model.leftEditor
                    );
                    this._activeMarkers.left = this._model.leftEditor.session.addMarker(
                        markersToHighlight.left.range,
                        leftMarkerClass,
                        "fullLine",
                        false
                    );
                }
            }
        }, 10);
    }

    _addActiveDifferenceMarkersInSideBySideView(markersToHighlight) {
        this._removeActiveDifferenceMarkers();
        // console.log("%c adding active markers in side by side", "background-color: green;");
        let {leftMarkerClass, rightMarkerClass} =
            this._getActiveDifferenceMarkerClass(markersToHighlight);
        this._activeMarkers.left = this._model.leftEditor.session.addMarker(
            markersToHighlight.left.range,
            leftMarkerClass,
            "fullLine",
            false
        );
        this._activeMarkers.right = this._model.rightEditor.session.addMarker(
            markersToHighlight.right.range,
            rightMarkerClass,
            "fullLine",
            false
        );
    }

    _removeActiveDifferenceMarkers() {
        // console.log("%c deleting active markers", "background-color: red;");
        if (this._activeMarkers && this._activeMarkers.left !== undefined) {
            this._model.leftEditor.session.removeMarker(this._activeMarkers.left);
        }
        if (this._activeMarkers && this._activeMarkers.right !== undefined) {
            if (this._model.sideBySideView) {
                this._model.rightEditor.session.removeMarker(this._activeMarkers.right);
            } else {
                this._model.leftEditor.session.removeMarker(this._activeMarkers.right);
            }
        }
        this._activeMarkers = {};
    }

    _calculateScrollDistances(markersToHighlight) {
        let leftMaxScrollUpLines = this._scrollUpMaxLines(this._model.leftEditor);
        let rightMaxScrollUpLines = this._scrollUpMaxLines(this._model.rightEditor);
        let leftDiffLineNumberFromVisibleTop =
            markersToHighlight.left.range.start.row -
            this._model.leftEditor.renderer.getFirstFullyVisibleRow();
        let rightDiffLineNumberFromVisibleTop =
            markersToHighlight.right.range.start.row -
            this._model.rightEditor.renderer.getFirstFullyVisibleRow();
        let scrollLinesToBringDiffsToSameVisibleRows = Math.abs(
            leftDiffLineNumberFromVisibleTop - rightDiffLineNumberFromVisibleTop
        );
        let {leftScrollDistance, rightScrollDistance} = this._adjustForFirstVisibleRowDistances();

        if (leftDiffLineNumberFromVisibleTop > rightDiffLineNumberFromVisibleTop) {
            if (leftMaxScrollUpLines >= scrollLinesToBringDiffsToSameVisibleRows) {
                leftScrollDistance +=
                    scrollLinesToBringDiffsToSameVisibleRows *
                    this._model.leftEditor.renderer.lineHeight;
            } else {
                leftScrollDistance +=
                    leftMaxScrollUpLines * this._model.leftEditor.renderer.lineHeight;
                scrollLinesToBringDiffsToSameVisibleRows =
                    scrollLinesToBringDiffsToSameVisibleRows - leftMaxScrollUpLines;
                let rightMaxScrollDownLines = this._model.rightEditor.getFirstVisibleRow();
                if (rightMaxScrollDownLines >= scrollLinesToBringDiffsToSameVisibleRows) {
                    rightScrollDistance = -(
                        scrollLinesToBringDiffsToSameVisibleRows *
                        this._model.rightEditor.renderer.lineHeight
                    );
                } else if (rightMaxScrollDownLines > 0) {
                    rightScrollDistance = -(
                        rightMaxScrollDownLines * this._model.rightEditor.renderer.lineHeight
                    );
                }
            }
        } else if (leftDiffLineNumberFromVisibleTop < rightDiffLineNumberFromVisibleTop) {
            if (rightMaxScrollUpLines >= scrollLinesToBringDiffsToSameVisibleRows) {
                rightScrollDistance =
                    scrollLinesToBringDiffsToSameVisibleRows *
                    this._model.rightEditor.renderer.lineHeight;
            } else {
                rightScrollDistance =
                    rightMaxScrollUpLines * this._model.rightEditor.renderer.lineHeight;
                scrollLinesToBringDiffsToSameVisibleRows -= rightMaxScrollUpLines;
                if (this._model.leftEditor.getFirstVisibleRow() > 0) {
                    let leftMaxScrollDownLines = this._model.leftEditor.getFirstVisibleRow();
                    if (leftMaxScrollDownLines >= scrollLinesToBringDiffsToSameVisibleRows) {
                        leftScrollDistance = -(
                            scrollLinesToBringDiffsToSameVisibleRows *
                            this._model.leftEditor.renderer.lineHeight
                        );
                    } else {
                        leftScrollDistance = -(
                            leftMaxScrollDownLines * this._model.leftEditor.renderer.lineHeight
                        );
                    }
                }
            }
        }
        return {leftScrollDistance: leftScrollDistance, rightScrollDistance: rightScrollDistance};
    }

    _adjustForFirstVisibleRowDistances() {
        let leftScrollDistance = 0,
            rightScrollDistance = 0;
        if (
            this._model.leftEditor.renderer.getFirstVisibleRow() !==
            this._model.leftEditor.renderer.getFirstFullyVisibleRow()
        ) {
            leftScrollDistance =
                this._model.leftEditor.renderer.lineHeight *
                    this._model.leftEditor.renderer.getFirstFullyVisibleRow() -
                this._model.leftEditor.session.getScrollTop();
        }
        if (
            this._model.rightEditor.renderer.getFirstVisibleRow() !==
            this._model.rightEditor.renderer.getFirstFullyVisibleRow()
        ) {
            rightScrollDistance =
                this._model.rightEditor.renderer.lineHeight *
                    this._model.rightEditor.renderer.getFirstFullyVisibleRow() -
                this._model.rightEditor.session.getScrollTop();
            if (rightScrollDistance === leftScrollDistance) {
                rightScrollDistance = 0;
                leftScrollDistance = 0;
            } else if (rightScrollDistance > leftScrollDistance) {
                rightScrollDistance -= leftScrollDistance;
            } else {
                leftScrollDistance -= rightScrollDistance;
            }
        }
        return {leftScrollDistance: leftScrollDistance, rightScrollDistance: rightScrollDistance};
    }

    _navigateToRow(row, editor) {
        editor.gotoLine(row + 1, 0, true);
    }

    _enableOrDisableNavigationButtons() {
        let markers = this._model.markers;
        let markersEmpty = _.isEmpty(markers);
        if (markersEmpty || this._currentHighlightedDifferenceIndex <= 0) {
            this._findPreviousButton.disable();
        } else {
            this._findPreviousButton.enable();
        }

        if (markersEmpty || this._currentHighlightedDifferenceIndex >= markers.length - 1) {
            this._findNextButton.disable();
        } else {
            this._findNextButton.enable();
        }
    }

    _scrollUpMaxLines(editor) {
        //let editorLastLine = editor.session.getLength()-1;
        return editor.session.getLength() - editor.renderer.getLastFullyVisibleRow() - 1;
    }

    _scrollDownMaxLines(editor) {
        return editor.renderer.getFirstFullyVisibleRow();
    }

    _foldRightEditorLines(leftFoldData) {
        let {startRow, endRow} = this._getTargetEditorFoldRange(leftFoldData);
        this._model.rightEditor.session.foldAll(startRow, endRow);
    }

    _unfoldRightEditorLines(leftFoldData) {
        let startRow = _.get(this._getTargetEditorFoldRange(leftFoldData), "startRow", -1);
        this._model.rightEditor.session.unfold(startRow);
    }

    _foldLeftEditorLines(rightFoldData) {
        let {startRow, endRow} = this._getTargetEditorFoldRange(rightFoldData, false);
        this._model.leftEditor.session.foldAll(startRow, endRow);
    }

    _unfoldLeftEditorLines(rightFoldData) {
        let startRow = _.get(this._getTargetEditorFoldRange(rightFoldData, false), "startRow", -1);
        this._model.leftEditor.session.unfold(startRow);
    }

    _getTargetEditorFoldRange(sourceFoldData, foldDataOnLeft = true) {
        let sourceFoldEditor = foldDataOnLeft ? "left" : "right";
        let targetFoldEditor = foldDataOnLeft ? "right" : "left";
        let foldRowToCompare = {};
        foldRowToCompare[sourceFoldEditor] = {range: {end: {row: sourceFoldData.range.start.row}}};
        let markerIndexBeforeFoldRow = _.sortedLastIndexBy(
            this._model.markers,
            foldRowToCompare,
            `${sourceFoldEditor}.range.end.row`
        );
        let targetFoldStartRow, targetFoldEndRow;
        if (markerIndexBeforeFoldRow > 0) {
            let markerBeforeFoldRow = this._model.markers[markerIndexBeforeFoldRow - 1];
            //find fold after right marker
            targetFoldStartRow =
                markerBeforeFoldRow[targetFoldEditor].range.end.row +
                FOLD_START_DISTANCE_FROM_MARKER;
            targetFoldEndRow =
                targetFoldStartRow +
                sourceFoldData.range.end.row -
                sourceFoldData.range.start.row +
                1;
        } else {
            //first fold on right editor
            targetFoldStartRow = FOLD_START_DISTANCE_FROM_MARKER - 1;
            targetFoldEndRow =
                this._model.markers[markerIndexBeforeFoldRow][targetFoldEditor].range.start.row -
                FOLD_START_DISTANCE_FROM_MARKER;
        }
        return {startRow: targetFoldStartRow, endRow: targetFoldEndRow};
    }

    _highlightMarkersAtMousePositionInSideBySideView(mousePosition, mouseOnLeftEditor = true) {
        let currentMarkersOnMouse = this._getMarkersAtMousePointer(
            mousePosition,
            mouseOnLeftEditor
        );

        if (currentMarkersOnMouse) {
            if (
                this._currentMarkersOnMouseSameAsHighlightedActiveMarkersInSideBySideView(
                    currentMarkersOnMouse
                )
            ) {
                // console.log("****** found active markers same as markers on mouse no change needed*****");
                return;
            }
            this._removeActiveDifferenceMarkers();
            let {leftMarkerClass, rightMarkerClass} =
                this._getActiveDifferenceMarkerClass(currentMarkersOnMouse);
            this._activeMarkers.left = this._model.leftEditor.session.addMarker(
                currentMarkersOnMouse.left.range,
                leftMarkerClass,
                "fullLine",
                false
            );
            this._activeMarkers.right = this._model.rightEditor.session.addMarker(
                currentMarkersOnMouse.right.range,
                rightMarkerClass,
                "fullLine",
                false
            );
            //if left marker not visible make it visible
        } else {
            this._removeActiveDifferenceMarkers();
        }
    }

    _removeMarkersInSideBySideView(newActiveMarkers) {
        if (_.isEmpty(newActiveMarkers)) {
            return;
        }
        newActiveMarkers.forEach((currentMarkers) => {
            this._model.leftEditor.session.removeMarker(currentMarkers.left);
            this._model.rightEditor.session.removeMarker(currentMarkers.right);
        });
    }

    _currentMarkersOnMouseSameAsHighlightedActiveMarkersInSideBySideView(currentMarkersOnMouse) {
        if (!this._activeMarkers || (!this._activeMarkers.left && !this._activeMarkers.right)) {
            return false;
        }
        let activeMarkerLeft =
            this._model.leftEditor.session.getMarkers(false)[this._activeMarkers.left];
        let activeMarkerRight =
            this._model.rightEditor.session.getMarkers(false)[this._activeMarkers.right];
        return (
            currentMarkersOnMouse.left.range.isEqual(activeMarkerLeft.range) &&
            currentMarkersOnMouse.right.range.isEqual(activeMarkerRight.range)
        );
    }

    _getMarkersAtMousePointer(mousePosition, mouseOnLeftEditor) {
        let currentRow = mousePosition.row;
        let currentColumn = 0;
        let compareElement = {};
        let compareMarker = mouseOnLeftEditor ? "left" : "right";
        _.set(compareElement, `${compareMarker}.range.end.row`, currentRow);
        let currentMarkersOnMouseIndex = _.sortedIndexBy(
            this._model.markers,
            compareElement,
            `${compareMarker}.range.end.row`
        );
        let currentMarkersOnMouse =
            currentMarkersOnMouseIndex >= 0 &&
            currentMarkersOnMouseIndex < this._model.markers.length
                ? this._model.markers[currentMarkersOnMouseIndex]
                : undefined;
        let mouseOnAMarker =
            currentMarkersOnMouse &&
            currentMarkersOnMouse[compareMarker].range.contains(currentRow, currentColumn);
        if (currentMarkersOnMouse && !mouseOnAMarker) {
            let markerLineClass = mouseOnLeftEditor ? "added-line" : "removed-line";
            mouseOnAMarker =
                currentRow === currentMarkersOnMouse[compareMarker].range.start.row - 1 &&
                currentMarkersOnMouse[compareMarker].clazz === markerLineClass;
        }
        let markersCount = this._model.markers.length;
        if (mouseOnAMarker && currentMarkersOnMouse[compareMarker].clazz.endsWith("line")) {
            let startIndex = currentMarkersOnMouseIndex + 1;
            while (
                startIndex < markersCount &&
                this._model.markers[startIndex][compareMarker].range.isEqual(
                    currentMarkersOnMouse[compareMarker].range
                ) &&
                this._model.markers[startIndex][compareMarker].clazz.endsWith("line")
            ) {
                startIndex++;
            }
            if (startIndex > currentMarkersOnMouseIndex + 1) {
                let notCompareMarker = !mouseOnLeftEditor ? "left" : "right";
                let lastMarkerOnMouseRange =
                    this._model.markers[startIndex - 1][notCompareMarker].range;
                let markerRange = new (ace.require("ace/range").Range)(
                    currentMarkersOnMouse[notCompareMarker].range.start.row,
                    0,
                    lastMarkerOnMouseRange.end.row,
                    lastMarkerOnMouseRange.end.column
                );
                currentMarkersOnMouse[notCompareMarker] = {
                    clazz: currentMarkersOnMouse[notCompareMarker].clazz,
                    range: markerRange,
                };
            } else if (currentMarkersOnMouseIndex < markersCount - 1) {
                let nextMarkerStartsOnMousePosition =
                    this._model.markers[startIndex][compareMarker].range.start.row ===
                    currentMarkersOnMouse[compareMarker].range.start.row;
                if (nextMarkerStartsOnMousePosition) {
                    let distanceFromRowTop;
                    if (mouseOnLeftEditor) {
                        distanceFromRowTop =
                            (this._leftEditorLastMouseY -
                                this._editorTop +
                                this._model.leftEditor.renderer.getScrollTop()) %
                            this._model.leftEditor.renderer.lineHeight;
                    } else {
                        distanceFromRowTop =
                            (this._rightEditorLastMouseY -
                                this._editorTop +
                                this._model.rightEditor.renderer.getScrollTop()) %
                            this._model.rightEditor.renderer.lineHeight;
                    }
                    if (distanceFromRowTop > this._model.leftEditor.renderer.lineHeight / 3) {
                        currentMarkersOnMouse = this._model.markers[currentMarkersOnMouseIndex + 1];
                    }
                }
            }
        }
        return mouseOnAMarker ? currentMarkersOnMouse : undefined;
    }

    _highlightMarkersAtMousePositionInSinglePaneView(mousePosition) {
        let currentRow = mousePosition.row;
        let currentMarkersOnMouseIndex = _.sortedIndexBy(
            this._model.markers,
            {left: {range: {end: {row: currentRow}}}},
            (currentMarkers) => {
                return currentMarkers.right
                    ? currentMarkers.right.range.end.row
                    : currentMarkers.left.range.end.row;
            }
        );
        if (
            currentMarkersOnMouseIndex >= 0 &&
            currentMarkersOnMouseIndex < this._model.markers.length
        ) {
            let currentMarkersOnMouse = this._model.markers[currentMarkersOnMouseIndex];
            let mouseOnAMarker =
                currentMarkersOnMouse.left &&
                currentMarkersOnMouse.left.range.contains(currentRow, 0);
            if (!mouseOnAMarker && currentMarkersOnMouse.right) {
                mouseOnAMarker = currentMarkersOnMouse.right.range.contains(currentRow, 0);
            }
            if (mouseOnAMarker) {
                if (
                    this._currentMarkersOnMouseSameAsHighlightedActiveMarkersInSinglePaneView(
                        currentMarkersOnMouse
                    )
                ) {
                    // console.log("****** current highlight markers same as markers on mouse no change ********");
                    return;
                }
                this._removeActiveDifferenceMarkers();
                //let isCurrentDifferenceUpdate = currentMarkersOnMouse.left && currentMarkersOnMouse.right;
                let {leftMarkerClass, rightMarkerClass} =
                    this._getActiveDifferenceMarkerClass(currentMarkersOnMouse);
                this._activeMarkers.left = currentMarkersOnMouse.left
                    ? this._model.leftEditor.session.addMarker(
                          currentMarkersOnMouse.left.range,
                          leftMarkerClass,
                          "fullLine",
                          false
                      )
                    : undefined;
                this._activeMarkers.right = currentMarkersOnMouse.right
                    ? this._model.leftEditor.session.addMarker(
                          currentMarkersOnMouse.right.range,
                          rightMarkerClass,
                          "fullLine",
                          false
                      )
                    : undefined;
            } else {
                this._removeActiveDifferenceMarkers();
            }
        } else {
            this._removeActiveDifferenceMarkers();
        }
    }

    _currentMarkersOnMouseSameAsHighlightedActiveMarkersInSinglePaneView(currentMarkersOnMouse) {
        if (!this._activeMarkers || (!this._activeMarkers.left && !this._activeMarkers.right)) {
            return false;
        }
        let markersEqual = this._activeMarkers.left ? false : true;
        if (this._activeMarkers.left) {
            if (!currentMarkersOnMouse.left) {
                markersEqual = false;
            } else {
                let activeMarkerLeft =
                    this._model.leftEditor.session.getMarkers(false)[this._activeMarkers.left];
                markersEqual =
                    activeMarkerLeft.range &&
                    currentMarkersOnMouse.left.range.isEqual(activeMarkerLeft.range);
            }
        }
        if (markersEqual && this._activeMarkers.right) {
            if (!currentMarkersOnMouse.right) {
                markersEqual = false;
            } else {
                let activeMarkerRight =
                    this._model.leftEditor.session.getMarkers(false)[this._activeMarkers.right];
                markersEqual = currentMarkersOnMouse.right.range.isEqual(activeMarkerRight.range);
            }
        }
        return markersEqual;
    }

    _getActiveDifferenceMarkerClass(markersToHighlight) {
        let leftMarkerClass, rightMarkerClass;
        if (this._model.sideBySideView) {
            leftMarkerClass = markersToHighlight.left.clazz + "-marker-border";
            rightMarkerClass = markersToHighlight.right.clazz + "-marker-border";
        } else {
            let isCurrentDifferenceUpdate = markersToHighlight.left && markersToHighlight.right;
            leftMarkerClass = isCurrentDifferenceUpdate
                ? "active-updated-left-marker"
                : "removed-marker-border";
            rightMarkerClass = isCurrentDifferenceUpdate
                ? "active-updated-right-marker"
                : "added-marker-border";
        }

        return {leftMarkerClass: leftMarkerClass, rightMarkerClass: rightMarkerClass};
    }
    /*******************************************/
    /* END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = JsonDiffNavigationRenderer;
